import React, { useState, useEffect, useCallback } from 'react';
import { suppliersAPI, SupplierProductCategory, Supplier, SupplierCatalogueProduct } from '../../services/api';
import { Card, CardBody, Button, Spinner, EmptyState, Badge } from '../ui';
import {
  AdjustmentsHorizontalIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CubeIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const CATALOGUE_PRODUCT_TYPE_LABELS: Record<string, string> = {
  PRODUCT: 'Product',
  RAW_MATERIAL: 'Raw Material',
  SPICES: 'Spices',
  HERBS: 'Herbs',
  OILS_AND_OIL_SEEDS: 'Oils & Oils Seeds',
  COSMETIC_AND_NATURAL_BEAUTY: 'Cosmetic & Natural Beauty',
  DRIED_FOOD: 'Dried Food',
  SEEDS: 'Seeds',
  EXTRACTS: 'Extracts',
  HERBAL_INFUSIONS: 'Herbal Infusions',
};

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const SupplierProductsSection: React.FC = () => {
  const [products, setProducts] = useState<SupplierCatalogueProduct[]>([]);
  const [categories, setCategories] = useState<SupplierProductCategory[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState<{ supplierId?: number; categoryId?: number; search?: string }>({});
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await suppliersAPI.getAllProducts({
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
      });
      setProducts(Array.isArray(response.data) ? response.data : []);
      setPagination(prev => ({ ...prev, ...response.pagination }));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  const fetchDropdownData = async () => {
    try {
      const [cats, sups] = await Promise.all([
        suppliersAPI.getCategories(),
        suppliersAPI.getAll({ limit: 100 }) // Assuming limit 100 gives a flat list or at least paginated first page for the dropdown
      ]);
      setCategories(cats);
      // Depending on API response structure:
      setSuppliers(sups.data || sups);
    } catch (err: any) {
      console.error('Failed to load filter dropdowns', err);
    }
  };

  useEffect(() => {
    fetchDropdownData();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      setFilters(prev => {
        const nextFilters = { ...prev };
        const normalizedSearch = searchInput.trim();

        if (normalizedSearch) {
          nextFilters.search = normalizedSearch;
        } else {
          delete nextFilters.search;
        }

        return nextFilters;
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleFilterChange = (key: 'supplierId' | 'categoryId', value: string) => {
    setPagination(prev => ({ ...prev, page: 1 }));
    setFilters(prev => {
      const newFilters = { ...prev };
      if (!value) {
        delete newFilters[key];
      } else {
        newFilters[key] = parseInt(value, 10);
      }
      return newFilters;
    });
  };

  const clearFilters = () => {
    setSearchInput('');
    setFilters({});
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    const lastPage = Math.max(pagination.pages, 1);
    setPagination(prev => ({ ...prev, page: Math.min(Math.max(page, 1), lastPage) }));
  };

  const handleLimitChange = (value: string) => {
    const limit = parseInt(value, 10);
    setPagination(prev => ({ ...prev, page: 1, limit }));
  };

  const hasActiveFilters = filters.supplierId || filters.categoryId || filters.search;
  const activeFilterCount = [filters.supplierId, filters.categoryId, filters.search].filter(Boolean).length;
  const pageCount = Math.max(pagination.pages, 1);
  const showingStart = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const showingEnd = Math.min(pagination.page * pagination.limit, pagination.total);
  const visibleProducts = Array.isArray(products) ? products : [];

  return (
    <div className="space-y-4">
      {error && (
        <Card variant="bordered" className="border-error-200 bg-error-50">
          <CardBody className="p-4">
            <div className="text-sm text-error-700">{error}</div>
          </CardBody>
        </Card>
      )}

      {/* Filters */}
      <Card variant="elevated" className="mb-4 overflow-hidden">
        <CardBody className="p-0">
          <div className="border-b border-neutral-200 bg-neutral-50/70 px-4 py-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <FunnelIcon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-neutral-900">Filter products</h3>
                  <p className="text-xs text-neutral-500">
                    {pagination.total} product{pagination.total === 1 ? '' : 's'}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {hasActiveFilters && (
                  <Badge variant="primary" size="sm">{activeFilterCount} active</Badge>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  leftIcon={<AdjustmentsHorizontalIcon className="h-4 w-4" />}
                >
                  {showFilters ? 'Hide filters' : 'More filters'}
                </Button>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    leftIcon={<XMarkIcon className="h-4 w-4" />}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="p-4">
            <div>
              <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-neutral-700">
                <MagnifyingGlassIcon className="h-4 w-4 text-neutral-400" />
                Product search
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-neutral-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Product, SKU, supplier, or category"
                  className="w-full rounded-lg border border-neutral-300 py-2.5 pl-10 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            {showFilters && (
              <div className="mt-4 grid grid-cols-1 gap-4 border-t border-neutral-200 pt-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Supplier</label>
                  <select
                    value={filters.supplierId || ''}
                    onChange={(e) => handleFilterChange('supplierId', e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  >
                    <option value="">All Suppliers</option>
                    {suppliers.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Category</label>
                  <select
                    value={filters.categoryId || ''}
                    onChange={(e) => handleFilterChange('categoryId', e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  >
                    <option value="">All Categories</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Table */}
      <Card variant="elevated" className="overflow-hidden">
        {loading && visibleProducts.length === 0 ? (
          <div className="flex justify-center items-center h-40"><Spinner size="lg" /></div>
        ) : visibleProducts.length === 0 ? (
          <EmptyState
            icon={<CubeIcon className="h-12 w-12" />}
            title="No products found"
            description="There are no supplier products matching your criteria."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Product Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">SKU</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Supplier</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Cost</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Lead Time</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {visibleProducts.map((p) => (
                    <tr key={p.id} className="hover:bg-neutral-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">{p.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">{p.sku || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{p.supplier?.name || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">{p.category?.name || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                        {p.productType?.name || (p.type ? (CATALOGUE_PRODUCT_TYPE_LABELS[p.type] || p.type.replace(/_/g, ' ')) : '-')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">
                        {p.cost ? `$${Number(p.cost).toFixed(2)}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">{p.leadTime || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="border-t border-neutral-200 px-4 py-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-neutral-700">
                  Showing <span className="font-medium">{showingStart}</span> to{' '}
                  <span className="font-medium">{showingEnd}</span> of{' '}
                  <span className="font-medium">{pagination.total}</span>
                </p>

                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-neutral-700">
                    Rows
                    <select
                      value={pagination.limit}
                      onChange={(e) => handleLimitChange(e.target.value)}
                      className="rounded-lg border border-neutral-300 px-2 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {PAGE_SIZE_OPTIONS.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </label>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1 || loading}
                      aria-label="Previous page"
                      title="Previous page"
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                    </Button>
                    <span className="min-w-[6rem] text-center text-sm text-neutral-700">
                      Page <span className="font-medium">{pagination.page}</span> of{' '}
                      <span className="font-medium">{pageCount}</span>
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pageCount || loading}
                      aria-label="Next page"
                      title="Next page"
                    >
                      <ChevronRightIcon className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default SupplierProductsSection;
